import { useMappingQuery } from 'api/MappingsAPI';
import { debounce } from 'lodash';
import { DEBOUNCE_TIMEOUTS_MS } from 'modules/customEvents/constants';
import { GenericDataType } from 'modules/domains';
import { addNegativeToastWithErrorId } from 'modules/toastContainer';
import { ReactNode, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { formatFieldTypeForDisplay } from '../utils';
import { UserMappingsContext } from './UserMappingsContext';

export const UserMappingsContextProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useDispatch();
  const {
    data: userMappingsResponse = [],
    isError: isErrorUserMappings,
    isLoading: isLoadingUserMappings,
    refetch,
  } = useMappingQuery(
    { dataType: GenericDataType.User, fetchLatest: true, filterOutHiddenFields: false },
    {
      onError: (error) => {
        dispatch(
          addNegativeToastWithErrorId(
            'Problem fetching user mappings',
            'UserMappingsContextProvider',
            error,
          ),
        );
      },
    },
  );

  /**
   * Because of ES1 (string) and ES5 (keyword) differences in string value
   * we want to map 'keyword' => 'string'
   *
   * Format here on initial response to work with a consistent format
   * throughout the module
   *
   * NOTE: when creating a new field we convert 'string' => 'keyword' to POST API
   */
  const userMappings = useMemo(
    () =>
      userMappingsResponse
        .sort((a, b) => a.fieldName.localeCompare(b.fieldName))
        .map((mapping) => {
          const fieldType = formatFieldTypeForDisplay(mapping.fieldType);
          return {
            ...mapping,
            fieldType,
          };
        }),
    [userMappingsResponse],
  );

  const refetchUserMappings = debounce(() => refetch(), DEBOUNCE_TIMEOUTS_MS.fetchMappings);

  const context = {
    isErrorUserMappings,
    isLoadingUserMappings,
    refetchUserMappings,
    userMappings,
  };

  return <UserMappingsContext.Provider value={context}>{children}</UserMappingsContext.Provider>;
};
